/**
 * A connector's mark, with the fallback chain the catalogue needs: the row's
 * own `iconUrl`, then the known fallback for its toolkit, then a generic glyph
 * when the remote image itself fails to load.
 *
 * @module components/connections/ConnectorIcon
 */
import { CloudIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import type { ConnectionView } from "../../state/connections";
import { resolveConnectorIconFallback } from "../../lib/connectorIcons";

export function ConnectorIcon({
  connection,
}: {
  readonly connection: Pick<ConnectionView, "iconUrl" | "id" | "toolkitSlug">;
}) {
  const fallbackIconUrl = useMemo(
    () =>
      resolveConnectorIconFallback({
        id: connection.id,
        toolkitSlug: connection.toolkitSlug ?? null,
      }),
    [connection.id, connection.toolkitSlug],
  );
  const [iconSrc, setIconSrc] = useState<string | null>(
    connection.iconUrl ?? fallbackIconUrl,
  );

  useEffect(() => {
    setIconSrc(connection.iconUrl ?? fallbackIconUrl);
  }, [connection.iconUrl, fallbackIconUrl]);

  if (!iconSrc) {
    return <CloudIcon className="size-4 text-muted-foreground" />;
  }

  return (
    <img
      alt=""
      className="size-full object-contain"
      src={iconSrc}
      onError={() => {
        // One step down the chain per failure, so a dead catalogue URL still
        // lands on the known mark before giving up on the glyph.
        setIconSrc(
          fallbackIconUrl && iconSrc !== fallbackIconUrl ? fallbackIconUrl : null,
        );
      }}
    />
  );
}
